import { useState, useEffect, useCallback, useRef, useMemo } from "preact/hooks"
import useSWR from "swr"
import { format, parseISO } from "date-fns"
import { Loader2, ChevronDown, ChevronUp, RefreshCw } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select"
import { DistributorSalesUpload } from "./distributor_sales_upload"
import { DistributorSalesCharts } from "./distributor_sales_charts"
import { DistributorSalesTable } from "./distributor_sales_table"
import { HierarchyPieModal } from "./distributor_sales_region_pie"
import {
  listDistributorReports,
  getChartData,
  getHierarchyData,
  getRetailerProfitability,
  getProfitabilityCost,
  patchProfitabilityCost,
} from "../api/distributor_sales_api"
import {
  DistributorSalesReport,
  ChartDataResponse,
  HierarchyResponse,
  HierarchyNode,
  RetailerProfitabilityRow,
} from "../types/distributor_sales"
import {
  DistributorSalesTabSource,
  SyncedWeek,
  distributor_source_label,
  is_synced_report,
  latest_shipments_summary,
  order_reports_for_selector,
  report_option_label,
  synced_report_for_range,
  synced_weeks,
} from "../utils/distributor_reports"

interface DistributorSalesSectionProps {
  className?: string
  /** Distributor whose uploads this section shows and accepts. Defaults to KeHE. */
  source?: DistributorSalesTabSource
}

// Get all node IDs from hierarchy for expand all
function getAllNodeIds(nodes: HierarchyNode[]): string[] {
  const ids: string[] = []
  for (const node of nodes) {
    ids.push(node.id)
    if (node.children && node.children.length > 0) {
      ids.push(...getAllNodeIds(node.children))
    }
  }
  return ids
}

// Find report with longest date range (fallback when no KeHE vendor sales)
function findLongestRangeReport(reports: DistributorSalesReport[]): DistributorSalesReport | null {
  if (reports.length === 0) return null
  
  return reports.reduce((longest, report) => {
    const range = report.end_date && report.start_date 
      ? new Date(report.end_date).getTime() - new Date(report.start_date).getTime()
      : 0
    const longestRange = longest.end_date && longest.start_date
      ? new Date(longest.end_date).getTime() - new Date(longest.start_date).getTime()
      : 0
    return range > longestRange ? report : longest
  })
}

/** Prefer newest `report_date`, then newest `created_at`. */
function compareReportsByRecency(a: DistributorSalesReport, b: DistributorSalesReport): number {
  const dateA = new Date(a.report_date).getTime()
  const dateB = new Date(b.report_date).getTime()
  if (dateB !== dateA) return dateB - dateA
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
}

/** Default table = the synced weeks (they supersede uploads), else most recent KeHE Chains & Stores (vendor_sales), or McLane whole-file summary; otherwise longest-range report. */
function findDefaultTableReport(reports: DistributorSalesReport[]): DistributorSalesReport | null {
  if (reports.length === 0) return null
  const synced = reports.find((r: DistributorSalesReport) => is_synced_report(r))
  if (synced) return synced
  const mclane_summary = latest_shipments_summary(reports)
  if (mclane_summary) return mclane_summary
  const keheVendor = reports.filter(
    (r: DistributorSalesReport) =>
      r.source === "kehe" &&
      (r.report_type === "vendor_sales" || r.report_type === "chains_and_stores")
  )
  if (keheVendor.length > 0) {
    return [...keheVendor].sort(compareReportsByRecency)[0]
  }
  return findLongestRangeReport(reports)
}

/** From-week / to-week pickers over the synced weeks. Choosing the first and last week is the whole span. */
function SyncedWeekRange({
  report,
  value,
  onChange,
}: {
  report: DistributorSalesReport
  value: SyncedWeek | null
  onChange: (range: SyncedWeek | null) => void
}) {
  const weeks = useMemo(
    () => (report.start_date && report.end_date ? synced_weeks(report.start_date, report.end_date) : []),
    [report.start_date, report.end_date]
  )
  if (weeks.length === 0) return null
  const start = value?.start_date ?? weeks[0].start_date
  const end = value?.end_date ?? weeks[weeks.length - 1].end_date

  function choose(next_start: string, next_end: string) {
    const whole = next_start === weeks[0].start_date && next_end === weeks[weeks.length - 1].end_date
    onChange(whole ? null : { start_date: next_start, end_date: next_end })
  }

  return (
    <>
      <div class="w-44">
        <label class="block text-sm font-medium text-gray-700 mb-1">From week</label>
        <Select value={start} onValueChange={(next: string) => choose(next, next > end ? weeks.find((w: SyncedWeek) => w.start_date === next)!.end_date : end)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {weeks.map((week: SyncedWeek) => (
              <SelectItem key={week.start_date} value={week.start_date}>
                {format(parseISO(week.start_date), "MMM d, yyyy")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div class="w-44">
        <label class="block text-sm font-medium text-gray-700 mb-1">To week ending</label>
        <Select value={end} onValueChange={(next: string) => choose(next < start ? weeks.find((w: SyncedWeek) => w.end_date === next)!.start_date : start, next)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {weeks.map((week: SyncedWeek) => (
              <SelectItem key={week.end_date} value={week.end_date}>
                {format(parseISO(week.end_date), "MMM d, yyyy")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  )
}

export function DistributorSalesSection({ className, source = "kehe" }: DistributorSalesSectionProps) {
  // Data state
  const [reports, setReports] = useState<DistributorSalesReport[]>([])
  const [chartData, setChartData] = useState<ChartDataResponse | null>(null)
  const [hierarchyData, setHierarchyData] = useState<HierarchyResponse | null>(null)
  
  // UI state
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const section_source = source
  const source_label = distributor_source_label(source)
  const [selectedRetailers, setSelectedRetailers] = useState<string[]>([])
  const [selectedSkus, setSelectedSkus] = useState<string[]>([])
  const [skuMode, setSkuMode] = useState(false)
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())
  const [isExpanded, setIsExpanded] = useState(true)
  const [tableReportId, setTableReportId] = useState<string | "">("")
  const [regionPieNode, setRegionPieNode] = useState<HierarchyNode | null>(null)
  const [profitabilityData, setProfitabilityData] = useState<RetailerProfitabilityRow[]>([])
  const [show_deduction_data, set_show_deduction_data] = useState(false)
  const [selling_cost_pct_draft, set_selling_cost_pct_draft] = useState("")
  const cost_save_timer_ref = useRef<ReturnType<typeof setTimeout> | null>(null)
  const skuModeRef = useRef(skuMode)
  const selectedSkusRef = useRef(selectedSkus)
  skuModeRef.current = skuMode
  selectedSkusRef.current = selectedSkus

  const selected_report = useMemo(
    () =>
      tableReportId ? reports.find((r: DistributorSalesReport) => r.id === tableReportId) ?? null : null,
    [tableReportId, reports]
  )
  // Synced weeks can be narrowed to any span of them; null is every week synced.
  const [week_range, set_week_range] = useState<SyncedWeek | null>(null)
  const table_report = useMemo(
    () =>
      selected_report && is_synced_report(selected_report) && week_range
        ? synced_report_for_range(selected_report, week_range.start_date, week_range.end_date)
        : selected_report,
    [selected_report, week_range]
  )
  // What every table request reads: the selected report, or the span of synced weeks chosen.
  const data_report_id = table_report?.id ?? ""
  const table_source_for_cost = table_report?.source

  const cost_swr_key =
    table_source_for_cost && table_source_for_cost !== "spins"
      ? ["distributor-profitability-cost", table_source_for_cost]
      : null
  const { data: profitability_cost, mutate: mutate_profitability_cost } = useSWR(cost_swr_key, () =>
    getProfitabilityCost(section_source)
  )

  useEffect(() => {
    if (profitability_cost == null) {
      set_selling_cost_pct_draft("")
      return
    }
    const p = profitability_cost.selling_cost_pct
    set_selling_cost_pct_draft(p === null || p === undefined || p === "" ? "" : String(p))
  }, [profitability_cost])

  useEffect(() => {
    return () => {
      if (cost_save_timer_ref.current) clearTimeout(cost_save_timer_ref.current)
    }
  }, [])

  const schedule_profitability_cost_save = useCallback(
    (pct_raw: string, overrides_snapshot: Record<string, number | string>, refetch_profitability: boolean) => {
      if (!table_source_for_cost || table_source_for_cost === "spins") return
      if (cost_save_timer_ref.current) clearTimeout(cost_save_timer_ref.current)
      cost_save_timer_ref.current = setTimeout(() => {
        cost_save_timer_ref.current = null
        const src = table_source_for_cost
        void (async () => {
          try {
            await patchProfitabilityCost({
              source: src,
              selling_cost_pct: pct_raw.trim() === "" ? null : pct_raw.trim(),
              selling_cost_overrides: overrides_snapshot,
            })
            await mutate_profitability_cost()
            if (refetch_profitability && data_report_id) {
              const profitability = await getRetailerProfitability({
                report_id: data_report_id,
                source: section_source,
              })
              setProfitabilityData(profitability.rows)
            }
          } catch (e) {
            console.error("Failed to save selling cost:", e)
          }
        })()
      }, 700)
    },
    [table_source_for_cost, mutate_profitability_cost, data_report_id, section_source]
  )

  const handle_selling_cost_override_change = useCallback(
    (retailer: string, dollars: number | null) => {
      const overrides = { ...(profitability_cost?.selling_cost_overrides ?? {}) }
      if (dollars == null) {
        delete overrides[retailer]
      } else {
        overrides[retailer] = dollars
      }
      schedule_profitability_cost_save(selling_cost_pct_draft, overrides, true)
    },
    [profitability_cost, selling_cost_pct_draft, schedule_profitability_cost_save]
  )

  // Load chart data (independent from table) - load ALL retailers/SKUs, filter client-side
  const loadChartData = useCallback(async () => {
    try {
      const charts = await getChartData(
        skuMode
          ? {
              source: section_source,
              by_sku: true,
              ...(selectedSkus.length > 0 ? { skus: selectedSkus.join(",") } : {}),
            }
          : { source: section_source }
      )
      setChartData(charts)
    } catch (err) {
      console.error("Error loading chart data:", err)
      setError(err instanceof Error ? err.message : "Failed to load chart data")
    }
  }, [skuMode, selectedSkus, section_source])

  // Load table hierarchy data and profitability (independent from chart)
  const loadHierarchyData = useCallback(async () => {
    if (!data_report_id) return

    try {
      const [hierarchy, profitability] = await Promise.all([
        getHierarchyData({ report_id: data_report_id, source: section_source }),
        getRetailerProfitability({
          report_id: data_report_id,
          source: section_source,
        }),
      ])
      setHierarchyData(hierarchy)
      setProfitabilityData(profitability.rows)
    } catch (err) {
      console.error("Error loading hierarchy data:", err)
      setError(err instanceof Error ? err.message : "Failed to load hierarchy data")
    }
  }, [data_report_id, section_source])

  // Initial load
  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const reportsList = (await listDistributorReports({ source: section_source })).filter(
        (report: DistributorSalesReport) => report.source === section_source
      )
      setReports(reportsList)
      
      // Set default table report: most recent KeHE vendor_sales, else longest-range
      let currentTableReportId = tableReportId
      if (reportsList.length > 0 && !currentTableReportId) {
        const defaultReport = findDefaultTableReport(reportsList)
        if (defaultReport) {
          currentTableReportId = defaultReport.id
          setTableReportId(defaultReport.id)
        }
      }
      
      // Load chart data — use refs so `loadData` deps need not include skuMode (avoid full reload on chart mode toggle)
      const sm = skuModeRef.current
      const skus = selectedSkusRef.current
      const charts = await getChartData(
        sm
          ? {
              source: section_source,
              by_sku: true,
              ...(skus.length > 0 ? { skus: skus.join(",") } : {}),
            }
          : { source: section_source }
      )
      setChartData(charts)
      
      // Load hierarchy data and profitability if we have a report selected
      if (currentTableReportId) {
        const [hierarchy, profitability] = await Promise.all([
          getHierarchyData({ report_id: currentTableReportId, source: section_source }),
          getRetailerProfitability({
            report_id: currentTableReportId,
            source: section_source,
          }),
        ])
        setHierarchyData(hierarchy)
        setProfitabilityData(profitability.rows)
      }
    } catch (err) {
      console.error("Error loading distributor sales data:", err)
      setError(err instanceof Error ? err.message : "Failed to load data")
    } finally {
      setLoading(false)
    }
  }, [tableReportId, section_source])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Reload chart data when source or SKU mode / SKU selection changes (after initial load)
  useEffect(() => {
    if (!loading && reports.length > 0) {
      loadChartData()
    }
  }, [loading, skuMode, selectedSkus, loadChartData])

  // Collapse tree when switching table report
  useEffect(() => {
    if (tableReportId && !loading && reports.length > 0) {
      setExpandedNodes(new Set())
    }
  }, [tableReportId, loading, reports.length])

  useEffect(() => {
    set_show_deduction_data(false)
    set_week_range(null)
  }, [tableReportId])

  // Reload hierarchy + profitability when table report, week span, source, or KeHE allocation toggle changes
  useEffect(() => {
    if (data_report_id && !loading && reports.length > 0) {
      loadHierarchyData()
    }
  }, [data_report_id, loading, reports.length, loadHierarchyData])

  // Handle table report selection change
  function handleTableReportChange(reportId: string) {
    setTableReportId(reportId)
  }

  // Handle expansion
  function toggleExpansion(nodeId: string) {
    setExpandedNodes((prev: Set<string>) => {
      const newSet = new Set(prev)
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId)
      } else {
        newSet.add(nodeId)
      }
      return newSet
    })
  }

  function expandAll() {
    if (hierarchyData?.nodes) {
      const allIds = getAllNodeIds(hierarchyData.nodes)
      setExpandedNodes(new Set(allIds))
    }
  }

  function collapseAll() {
    setExpandedNodes(new Set())
  }

  // Check if we have any data
  const hasData = reports.length > 0

  return (
    <div class={`bg-white border border-gray-200 rounded-lg mb-8 ${className || ""}`}>
      {/* Section Header */}
      <div 
        class="flex items-center justify-between px-6 py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div class="flex items-center gap-3">
          <h2 class="text-xl font-semibold">{source_label} Sales Data</h2>
          {reports.length > 0 && (
            <span class="text-sm text-gray-500">
              ({reports.length} report{reports.length !== 1 ? "s" : ""})
            </span>
          )}
        </div>
        <div class="flex items-center gap-2">
          <Button
            variant="ghost"
            // @ts-ignore
            size="sm"
            onClick={(e: Event) => {
              e.stopPropagation()
              loadData()
            }}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Section Content */}
      {isExpanded && (
        <div class="p-6">
          {loading ? (
            <div class="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              <span class="ml-3 text-gray-500">Loading {source_label} sales data...</span>
            </div>
          ) : error ? (
            <div class="text-center py-8">
              <p class="text-red-600 mb-4">{error}</p>
              <Button onClick={loadData} variant="outline">
                Retry
              </Button>
            </div>
          ) : (
            <div class="space-y-6">
              {/* Upload when empty */}
              {!hasData && (
                <DistributorSalesUpload
                  sourceLocked={section_source}
                  onUploadComplete={loadData}
                  className="w-full max-w-md"
                />
              )}

              {hasData ? (
                <>
                  {/* Charts */}
                  <DistributorSalesCharts
                    data={chartData}
                    selectedRetailers={selectedRetailers}
                    onRetailersChange={setSelectedRetailers}
                    selectedSkus={selectedSkus}
                    onSkusChange={setSelectedSkus}
                    skuMode={skuMode}
                    onSkuModeChange={(mode: boolean) => {
                      setSkuMode(mode)
                      setSelectedSkus([])
                    }}
                    source={section_source}
                    reportId={data_report_id || undefined}
                  />

                  {/* Table */}
                  <div class="space-y-4">
                    {/* Table Report Selector */}
                    {reports.length > 0 && (
                      <div class="flex flex-wrap gap-6 items-end">
                        <div class={source === "mclane" ? "w-96" : "w-64"}>
                          <label class="block text-sm font-medium text-gray-700 mb-1">Table Report</label>
                          <Select
                            value={tableReportId}
                            onValueChange={handleTableReportChange}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select report..." />
                            </SelectTrigger>
                            <SelectContent>
                              {order_reports_for_selector(reports).map((report: DistributorSalesReport) => (
                                <SelectItem key={report.id} value={report.id}>
                                  {report_option_label(report)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {selected_report && is_synced_report(selected_report) ? (
                          <SyncedWeekRange report={selected_report} value={week_range} onChange={set_week_range} />
                        ) : null}
                      </div>
                    )}
                    {show_deduction_data && table_report && table_source_for_cost !== "spins" ? (
                      <div class="flex flex-wrap items-end gap-4 rounded-lg border border-orange-200 bg-orange-50/60 px-4 py-3">
                        <div class="w-40">
                          <label class="block text-sm font-medium text-gray-800 mb-1">
                            COGS % of shipped vendor cost
                          </label>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={selling_cost_pct_draft}
                            onInput={(e: Event) => {
                              const v = (e.target as HTMLInputElement).value
                              set_selling_cost_pct_draft(v)
                              if (profitability_cost === undefined && cost_swr_key) return
                              schedule_profitability_cost_save(
                                v,
                                profitability_cost?.selling_cost_overrides ?? {},
                                true
                              )
                            }}
                          />
                        </div>
                        <p class="text-xs text-gray-600 max-w-md pb-2">
                          Applies to all {source_label} reports. Click a COGS cell in the table to
                          override per retailer.
                        </p>
                      </div>
                    ) : null}
                    <DistributorSalesTable
                      data={hierarchyData}
                      expandedNodes={expandedNodes}
                      onToggleExpansion={toggleExpansion}
                      onExpandAll={expandAll}
                      onCollapseAll={collapseAll}
                      onOpenHierarchyPie={(node: HierarchyNode) => setRegionPieNode(node)}
                      profitabilityData={profitabilityData}
                      show_deduction_data={show_deduction_data}
                      on_show_deduction_data_change={set_show_deduction_data}
                      retailer_mappings_report_id={
                        table_source_for_cost !== "spins" ? data_report_id || null : null
                      }
                      export_report_id={data_report_id || null}
                      export_source={section_source}
                      on_selling_cost_override_change={handle_selling_cost_override_change}
                      deductionWindowReport={table_report}
                    />
                    {regionPieNode && (
                      <HierarchyPieModal
                        node={regionPieNode}
                        reportId={data_report_id || undefined}
                        onClose={() => setRegionPieNode(null)}
                      />
                    )}
                    
                    {/* Upload when we have data - at bottom of table */}
                    <details class="border border-gray-200 rounded-lg">
                      <summary class="px-4 py-3 cursor-pointer hover:bg-gray-50 font-medium">
                        Upload New Report
                      </summary>
                      <div class="p-4 border-t border-gray-200">
                        <DistributorSalesUpload
                          sourceLocked={section_source}
                          onUploadComplete={loadData}
                          className="max-w-md"
                        />
                      </div>
                    </details>
                  </div>
                </>
              ) : (
                <div class="text-center py-8 text-gray-500">
                  <p class="mb-2">No {source_label} sales data yet.</p>
                  <p class="text-sm">
                    {source === "mclane"
                      ? "Upload a McLane sales export (weekly shipments by customer and item) to get started."
                      : "Upload a KeHE Vendor Sales or Chargebacks report to get started."}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
